import type { ReactNode } from 'react';

import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import CheckroomRoundedIcon from '@mui/icons-material/CheckroomRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import LocalMallRoundedIcon from '@mui/icons-material/LocalMallRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import TelegramIcon from '@mui/icons-material/Telegram';
import DomainRoundedIcon from '@mui/icons-material/DomainRounded';
import PermMediaRoundedIcon from '@mui/icons-material/PermMediaRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import ShoppingBasketRoundedIcon from '@mui/icons-material/ShoppingBasketRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';

import type { PermissionCode } from '@/shared/permissions';
import type { MerchantPortalSettingsSectionKey } from '@/shared/navigation/merchantPortalNavVisibility';

import { STORE_SETTINGS_PERMISSIONS } from '../model/permissions';
import type { StoreSettingsDomain } from '../model/permissions';

export interface SettingsNavItem {
  /** Stable domain key (matches `STORE_SETTINGS_PERMISSIONS`). */
  key: StoreSettingsDomain;
  /** Short menu label. */
  label: string;
  /** One-line description for the settings landing grid. */
  description: string;
  /** Route segment relative to `/settings`. */
  segment: string;
  /** Absolute route path. */
  path: string;
  /** Section icon. */
  icon: ReactNode;
  /** Permission required to open/view the screen. */
  viewPermission: PermissionCode;
  /** Permission required to save changes. */
  managePermission: PermissionCode;
  /** When true, the channel/domain is a preview of an upcoming capability (no backend yet). */
  preview?: boolean;
  /** When true, the item is kept in code but hidden from the settings UI. */
  hiddenFromUi?: boolean;
}

function item(
  key: StoreSettingsDomain,
  label: string,
  description: string,
  segment: string,
  icon: ReactNode,
  preview = false,
  hiddenFromUi = false,
): SettingsNavItem {
  return {
    key,
    label,
    description,
    segment,
    path: `/settings/${segment}`,
    icon,
    viewPermission: STORE_SETTINGS_PERMISSIONS[key].view,
    managePermission: STORE_SETTINGS_PERMISSIONS[key].manage,
    preview,
    hiddenFromUi,
  };
}

/** Ordered navigation model for every settings domain. */
export const SETTINGS_NAV: SettingsNavItem[] = [
  item(
    'delivery',
    'Delivery',
    'Delivery models, providers, credentials and fallbacks.',
    'delivery',
    <LocalShippingRoundedIcon />,
  ),
  item(
    'payments',
    'Payments',
    'Payment methods, capture mode, providers and fiscalisation.',
    'payments',
    <CreditCardRoundedIcon />,
  ),
  item(
    'checkout',
    'Checkout',
    'Guest checkout, required fields, steps and selection rules.',
    'checkout',
    <ShoppingCartCheckoutRoundedIcon />,
  ),
  item(
    'orders',
    'Orders',
    'OMS mode, auto-cancel, confirmation and lifecycle transitions.',
    'orders',
    <ReceiptLongRoundedIcon />,
  ),
  item(
    'pricing',
    'Pricing',
    'Pricing/promotion engine mode, rounding and external engine.',
    'pricing',
    <SellRoundedIcon />,
  ),
  item(
    'catalog',
    'Catalog',
    'Publication, visibility, search, SEO defaults and media rules.',
    'catalog',
    <Inventory2RoundedIcon />,
  ),
  item(
    'customers',
    'Customers',
    'Registration, verification, profile fields and consent.',
    'customers',
    <GroupsRoundedIcon />,
  ),
  item(
    'notifications',
    'Notifications',
    'Channels, templates, opt-in and notification providers.',
    'notifications',
    <NotificationsRoundedIcon />,
  ),
  item(
    'content',
    'Content',
    'Storefront pages, legal content, banners and SEO metadata.',
    'content',
    <ArticleRoundedIcon />,
  ),
  item(
    'engagement',
    'Engagement',
    'Reminders, abandoned cart, campaigns and frequency limits.',
    'engagement',
    <CampaignRoundedIcon />,
  ),
  item(
    'telegram',
    'Telegram',
    'Messaging rules, formatting and delivery configuration for the Telegram channel.',
    'telegram',
    <TelegramIcon />,
  ),
  item(
    'max',
    'MAX',
    'MAX messenger bot, Mini App URL and message preferences.',
    'max',
    <ChatRoundedIcon />,
  ),
  item(
    'web',
    'Web',
    'Your responsive online store and PWA storefront.',
    'web',
    <LanguageRoundedIcon />,
    true,
    true,
  ),
  item(
    'ozon',
    'Ozon',
    'Sell on the Ozon marketplace — listings, orders and fulfilment.',
    'ozon',
    <ShoppingBagRoundedIcon />,
    true,
    true,
  ),
  item(
    'wildberries',
    'Wildberries',
    'Sell on the Wildberries marketplace — listings, orders and fulfilment.',
    'wildberries',
    <LocalMallRoundedIcon />,
    true,
    true,
  ),
  item(
    'yandex-market',
    'Yandex Market',
    'Sell on Yandex Market — listings, orders and fulfilment.',
    'yandex-market',
    <ShoppingCartRoundedIcon />,
    true,
    true,
  ),
  item(
    'lamoda',
    'Lamoda',
    'Sell fashion on the Lamoda marketplace.',
    'lamoda',
    <CheckroomRoundedIcon />,
    true,
    true,
  ),
  item(
    'tenant',
    'Company',
    'Legal entity profile, bank accounts and storefront identity.',
    'tenant',
    <DomainRoundedIcon />,
  ),
  item(
    'store',
    'Store profile',
    'Store and pickup locations and the operational profile.',
    'store',
    <StoreRoundedIcon />,
  ),
  item(
    'address',
    'Address',
    'Address format, validation and the geocoding provider.',
    'address',
    <PlaceRoundedIcon />,
  ),
  item(
    'inventory',
    'Inventory',
    'Stock tracking, reservation and availability policy.',
    'inventory',
    <WarehouseRoundedIcon />,
  ),
  item(
    'media',
    'Media',
    'Image formats, variants, banners and CDN.',
    'media',
    <PermMediaRoundedIcon />,
  ),
  item(
    'ai-agents',
    'AI agents',
    'AI assistants, autonomy and the model provider.',
    'ai-agents',
    <SmartToyRoundedIcon />,
  ),
  item(
    'basket',
    'Basket',
    'Cart lifetime, limits and guest-merge behaviour.',
    'basket',
    <ShoppingBasketRoundedIcon />,
  ),
];

/** Fast lookup of a settings nav item by its domain key. */
export const SETTINGS_NAV_BY_KEY = Object.fromEntries(
  SETTINGS_NAV.map((nav) => [nav.key, nav]),
) as Record<StoreSettingsDomain, SettingsNavItem>;

export interface SettingsGroup {
  /** Stable group key. */
  key: string;
  /** Group heading shown in the settings rail and overview. */
  label: string;
  /** One-line description for the overview group header. */
  description: string;
  /** Group icon. */
  icon: ReactNode;
  /** Ordered settings domains that belong to this group. */
  domains: StoreSettingsDomain[];
  /** Optional feature flag key that can hide the whole group from the UI. */
  featureKey?: MerchantPortalSettingsSectionKey;
}

/**
 * Grouped settings information architecture.
 *
 * The 19 settings domains are organised into a handful of meaningful categories
 * so the settings area is scannable instead of a flat wall of tiles. Every domain
 * appears in exactly one group; the order here drives both the settings rail and
 * the grouped overview.
 */
export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    key: 'store',
    label: 'Store',
    description: 'Store identity and tenant-level configuration.',
    icon: <StorefrontRoundedIcon />,
    domains: ['tenant'],
  },
  {
    key: 'sales-channels',
    label: 'Sales Channels',
    description:
      'Where you sell — Telegram (available) plus web, messenger and marketplace channels (preview).',
    icon: <ForumRoundedIcon />,
    domains: ['telegram', 'max', 'web', 'ozon', 'wildberries', 'yandex-market', 'lamoda'],
    featureKey: 'salesChannels',
  },
  {
    key: 'ai',
    label: 'AI',
    description: 'AI assistants and the model provider.',
    icon: <SmartToyRoundedIcon />,
    domains: ['ai-agents'],
    featureKey: 'ai',
  },
  {
    key: 'customers',
    label: 'Customers & marketing',
    description: 'Customer accounts, notifications, content and campaigns.',
    icon: <GroupsRoundedIcon />,
    domains: ['customers', 'notifications', 'content', 'engagement'],
    featureKey: 'customersMarketing',
  },
  {
    key: 'selling',
    label: 'Selling',
    description: 'Payments, checkout, cart and pricing rules.',
    icon: <CreditCardRoundedIcon />,
    domains: ['payments', 'checkout', 'basket', 'pricing'],
    featureKey: 'selling',
  },
  {
    key: 'fulfillment',
    label: 'Fulfillment',
    description: 'Delivery, store/pickup locations, addresses, order lifecycle and inventory.',
    icon: <LocalShippingRoundedIcon />,
    domains: ['delivery', 'store', 'address', 'orders', 'inventory'],
    featureKey: 'fulfilment',
  },
  {
    key: 'catalog',
    label: 'Catalog & media',
    description: 'Storefront catalog behaviour and media/CDN rules.',
    icon: <Inventory2RoundedIcon />,
    domains: ['catalog', 'media'],
    featureKey: 'catalogMedia',
  },
];

/** Resolve a group's domains to their (ordered) settings nav items. */
export function settingsNavForGroup(group: SettingsGroup): SettingsNavItem[] {
  return group.domains.map((domain) => SETTINGS_NAV_BY_KEY[domain]);
}
